import { Tabs, Descriptions } from 'antd';
import Modal from 'react-bootstrap/Modal';
import Initiative from './Initiative';
import { useEffect, useState, useRef } from 'react';
import useTopRemark from './useTopRemark';
import { FaCheckCircle } from "react-icons/fa";
import { toast } from 'react-toastify';
import EditConfirmationModal from '../../../../components/modal/EditConfirmationModal';
import ActionButtonGradient from '../../../../components/button/ActionButtonGradient';

function TopRemark({
    show, 
    handleClose, 
    user,
    calibratedScore,
    setCalibratedScore,
    id,
    phaseBefore,
    project,
    watch
  }) {
  

  const {
    payload,
    items,
    setItems,
    setPayload,
    resetToInitialState,
    handleOnChanges,
    onDeleteInitiative,
    onClickAddInitiative,
    getTopRemarks,
    postTopRemarks,
    deleteTopRemark,
    checkBeforePostInitiative,
    handleFileChange,
    activeKey,
    onChangeActiveKey,
    isSaving,
  } = useTopRemark(`${user.ID || user.Nik || user.Name}`)

  const [confirmationAPlus, setConfirmationAPlus] = useState(false)
  const [isAPlus, setIsAPlus] = useState(false)
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [originalFormData, setOriginalFormData] = useState(null)
  const [hasExistingJustification, setHasExistingJustification] = useState(false)
  
  // Track if data has been initialized to prevent saving empty initial state
  const isInitialized = useRef(false);
  // Track current user to detect user changes
  const currentUserRef = useRef(null);
  // Track if data was loaded from localStorage (don't overwrite with API data)
  const loadedFromLocalStorage = useRef(false);
  // Track if we just loaded data to prevent immediate re-save
  const justLoaded = useRef(false);

  // Reset state when user changes
  useEffect(() => {
    const currentUserKey = `${user.ID || user.Nik || user.Name}`;
    if (currentUserRef.current && currentUserRef.current !== currentUserKey) {
      resetToInitialState();
      setIsAPlus(false);
      setConfirmationAPlus(false);
      isInitialized.current = false;
      loadedFromLocalStorage.current = false;
    }
    currentUserRef.current = currentUserKey;
  }, [user, resetToInitialState]);

  useEffect(() =>{
    // Only run when modal opens (show becomes true)
    if (!show) {
      isInitialized.current = false; // Reset when modal closes
      // Reset all state variables when modal closes
      setHasUnsavedChanges(false);
      setOriginalFormData(null);
      setHasExistingJustification(false);
      return;
    }
    
    // Reset all tracking states at the start of each modal open
    loadedFromLocalStorage.current = false;
    setHasUnsavedChanges(false);
    setOriginalFormData(null);
    setHasExistingJustification(false);
    
    const storageKey = `topRemark_${user.ProjectID}_${user.EmployeeID}_${user.ProjectPhaseID}_${user.ID || user.Nik || user.Name.replace(/\s+/g, '_')}`;
    
    // Clean up old localStorage entries with the old key format to prevent conflicts
    const oldStorageKey = `topRemark_${user.ProjectID}_${user.EmployeeID}_${user.ProjectPhaseID}`;
    if (localStorage.getItem(oldStorageKey)) {
      localStorage.removeItem(oldStorageKey);
    }
    
    const savedData = localStorage.getItem(storageKey);
    
    // Check localStorage FIRST before calling API
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        
        if (parsedData && parsedData.initiatives && parsedData.initiatives.length > 0) {
          // Check if there's actual unsaved data (not just empty objects)
          const hasActualData = parsedData.initiatives.some(initiative => 
            Object.values(initiative).some(value => 
              value !== null && value !== undefined && value !== ''
            )
          );
          
          // Only restore from localStorage if it's a draft (no ID or empty ID)
          const isDraft = parsedData.initiatives.some(initiative => 
            !initiative.ID || initiative.ID === ''
          );
          
          if (hasActualData && isDraft) {
            // Restore saved initiatives (text and date fields only, no files)
            setPayload(parsedData.initiatives);
            setItems(parsedData.initiatives.length);
            
            // Mark as initialized since we're restoring from localStorage
            isInitialized.current = true;
            loadedFromLocalStorage.current = true; // Flag that we loaded from localStorage
            justLoaded.current = true; // Prevent immediate re-save
            
            // Show notification that draft was restored
            toast.info('Unsaved draft restored from your last session', { autoClose: 3000 });
            
            // Set A+ confirmation state
            if (calibratedScore[id].CalibrationRating === "A+") {
              setConfirmationAPlus(true)
              setIsAPlus(true)
            }else{
              setConfirmationAPlus(false)
              setIsAPlus(false)
            }
            return; // Skip API call
          }
        }
      } catch (error) {
        console.error('Error loading saved top remark:', error);
      }
    }
    
    // Only call API if no saved data exists
    setItems()
    getTopRemarks(user.ProjectID, user.EmployeeID, user.ProjectPhaseID, phaseBefore).then(() => {
      // Mark as initialized after API completes
      isInitialized.current = true;
      // Don't mark as loaded from localStorage since this came from API
      loadedFromLocalStorage.current = false;
      justLoaded.current = true; // Prevent immediate re-save of API data
    });
    
    // console.log("value top remarks= ", calibratedScore[id].CalibrationRating == "A+")
    if (calibratedScore[id].CalibrationRating === "A+") {
      setConfirmationAPlus(true)
      setIsAPlus(true)
    }else{
      setConfirmationAPlus(false)
      setIsAPlus(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, user.ProjectID, user.EmployeeID, user.ProjectPhaseID, user.ID, user.Nik, user.Name])

  // Store original form data after payload is updated from API
  useEffect(() => {
    // Only store original data when:
    // 1. Modal is open
    // 2. Data is initialized (API call completed)
    // 3. We didn't load from localStorage
    // 4. We have payload data
    // 5. justLoaded flag is true (meaning this is right after API call)
    if (show && isInitialized.current && !loadedFromLocalStorage.current && justLoaded.current && payload && payload.length > 0) {
      // Check if API returned existing justification data
      const hasApiData = payload.some(initiative => 
        initiative.ID || initiative.Initiative || initiative.Description || initiative.Result || initiative.Comment
      );
      setHasExistingJustification(hasApiData);
      
      // Store original form data from API for comparison
      setOriginalFormData(JSON.parse(JSON.stringify(payload)));
      justLoaded.current = false;
    }
  }, [show, payload, user]);



  // Save to localStorage whenever payload changes (but only after initialization)
  useEffect(() => {
    if (show && isInitialized.current && payload && payload.length > 0 && !justLoaded.current) {
      const storageKey = `topRemark_${user.ProjectID}_${user.EmployeeID}_${user.ProjectPhaseID}_${user.ID || user.Nik || user.Name.replace(/\s+/g, '_')}`;
      
      // Check if this is an unsaved draft (no ID means it hasn't been saved to server yet)
      const hasUnsavedData = payload.some(initiative => 
        !initiative.ID || initiative.ID === ''
      );
      
      // Check if user has entered any data
      const hasUserData = payload.some(init => 
        init.Initiative || init.Description || init.Result || init.Comment
      );
      
      // Only save to localStorage if:
      // 1. There's unsaved data, AND
      // 2. Either we loaded from localStorage OR the user has made changes (not just initial API load)
      const shouldSave = hasUnsavedData && (loadedFromLocalStorage.current || hasUserData);
      
      if (shouldSave) {
        try {
          // Remove File objects before saving (only save text and date fields)
          const initiativesWithoutFiles = payload.map(initiative => ({
            ...initiative,
            Evidence: undefined // Don't save File objects to localStorage
          }));
          
          const dataToSave = { 
            initiatives: initiativesWithoutFiles,
            timestamp: new Date().toISOString() // Track when saved
          };
          
          localStorage.setItem(storageKey, JSON.stringify(dataToSave));
          
          // Clean up old localStorage entries to prevent cross-user contamination
          const oldStorageKey = `topRemark_${user.ProjectID}_${user.EmployeeID}_${user.ProjectPhaseID}`;
          if (localStorage.getItem(oldStorageKey)) {
            localStorage.removeItem(oldStorageKey);
          }
        } catch (error) {
          console.error('Error saving to localStorage:', error);
          // If storage is full, clear old drafts
          if (error.name === 'QuotaExceededError') {
            console.warn('localStorage quota exceeded, clearing old drafts');
            clearOldDrafts();
          }
        }
      }
    }
  }, [payload, show, user]);

  // Check if form has been modified from original API data
  useEffect(() => {
    // Only check for changes if modal is open and we have both original and current data
    if (!show || !originalFormData || !payload || loadedFromLocalStorage.current || !isInitialized.current || justLoaded.current) {
      setHasUnsavedChanges(false);
      return;
    }
    
    const formHasChanged = JSON.stringify(payload) !== JSON.stringify(originalFormData);
    setHasUnsavedChanges(formHasChanged);
  }, [payload, originalFormData, show]);

  // Helper function to clear old draft data from localStorage
  const clearOldDrafts = () => {
    try {
      const keys = Object.keys(localStorage);
      const topRemarkKeys = keys.filter(key => key.startsWith('topRemark_'));
      
      // Sort by timestamp and keep only recent 5 drafts
      const draftsWithTimestamp = topRemarkKeys.map(key => {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          return {
            key,
            timestamp: data.timestamp || 0
          };
        } catch {
          return { key, timestamp: 0 };
        }
      }).sort((a, b) => b.timestamp - a.timestamp);
      
      // Remove old drafts beyond the 5 most recent
      draftsWithTimestamp.slice(5).forEach(draft => {
        localStorage.removeItem(draft.key);
      });
    } catch (error) {
      console.error('Error clearing old drafts:', error);
    }
  };



  const handleSave = () => {
    let bool = checkBeforePostInitiative(isAPlus);
    
    if (bool) {
      deleteTopRemark();
      postTopRemarks(user.ProjectID, user.EmployeeID, user.ProjectPhaseID, handleClose);
      let newPayload = {...calibratedScore};
      newPayload[id].FilledTopBottomMark = true;
      setCalibratedScore(newPayload);
      
      // Clear localStorage after successful save
      const storageKey = `topRemark_${user.ProjectID}_${user.EmployeeID}_${user.ProjectPhaseID}_${user.ID || user.Nik || user.Name.replace(/\s+/g, '_')}`;
      localStorage.removeItem(storageKey);
      
      // Clean up old localStorage entries as well
      const oldStorageKey = `topRemark_${user.ProjectID}_${user.EmployeeID}_${user.ProjectPhaseID}`;
      if (localStorage.getItem(oldStorageKey)) {
        localStorage.removeItem(oldStorageKey);
      }
      
      // Reset unsaved changes tracking
      setHasUnsavedChanges(false);
      setOriginalFormData(null);
    }
  };

  const handleModalClose = () => {
    // Check if there are unsaved changes and form has existing justification from API
    if (hasUnsavedChanges && hasExistingJustification && !loadedFromLocalStorage.current) {
      setShowUnsavedChangesModal(true);
    } else {
      handleClose();
    }
  };

  const handleConfirmSave = () => {
    // For existing justification (from API): Save to database
    if (hasExistingJustification) {
      setShowUnsavedChangesModal(false); // Dismiss confirmation modal first
      handleSave(); // Save to database (this will also close the main modal)
    } else {
      // For new justification: Save to localStorage as draft
      const storageKey = `topRemark_${user.ProjectID}_${user.EmployeeID}_${user.ProjectPhaseID}_${user.ID || user.Nik || user.Name.replace(/\s+/g, '_')}`;
      
      try {
        // Remove File objects before saving (only save text and date fields)
        const initiativesWithoutFiles = payload.map(initiative => ({
          ...initiative,
          Evidence: undefined // Don't save File objects to localStorage
        }));
        
        const dataToSave = { 
          initiatives: initiativesWithoutFiles,
          timestamp: new Date().toISOString()
        };
        
        localStorage.setItem(storageKey, JSON.stringify(dataToSave));
      } catch (error) {
        console.error('Error saving to localStorage:', error);
      }
      
      setShowUnsavedChangesModal(false);
      handleClose();
    }

    setOriginalFormData(null);
  };

  const handleDiscardChanges = () => {
    // Remove any draft data from localStorage
    const storageKey = `topRemark_${user.ProjectID}_${user.EmployeeID}_${user.ProjectPhaseID}_${user.ID || user.Nik || user.Name.replace(/\s+/g, '_')}`;
    localStorage.removeItem(storageKey);
    
    setShowUnsavedChangesModal(false);
    setHasUnsavedChanges(false);
    handleClose();

    setOriginalFormData(null);
  };

  return (
    <>
      {
        confirmationAPlus ?
        <Modal show={show} onHide={handleClose} dialogClassName='spmo-justification-modal' backdrop="static" centered>
          <Modal.Header style={{ display: "flex", justifyContent:"center", alignItems: "center", flexDirection: "column" }}>
            <Modal.Title>
              <div >
                <b>A+ Calibration Confirmation</b>
                <p style={{ fontSize: "16px", marginBottom: "0" }}>You are about to calibrate this employee to an A+ rating</p>
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div align="left">
              <p>To qualify for an A+ rating, the employee must meet<b> at least 1 out of the 2</b> criteria below.</p>
              <div className='flex-row'>
                <div style={{ marginRight: "10px" }}>
                  <FaCheckCircle fontSize={40} color={"#02B4CC"}/>
                </div>
                <div>
                  <p style={{ marginBottom: "0px" }}><b>New Innovation Project or Initiative</b></p>
                  <p>Delivered new innovative solutions that redefine processes or elevate business outcomes to new levels with <b>high tangible impact*</b> and consider their BU performance. The employee must be the leader and/or key person of the innovation implemented.</p>
                </div>
              </div>
              <div className='flex-row'>
                <div style={{ marginRight: "10px" }}>
                  <FaCheckCircle fontSize={40} color={"#02B4CC"}/>
                </div>
                <div>
                  <p style={{ marginBottom: "0px" }}><b>Beyond Job Scope Project or Initiative</b></p>
                  <p>Demonstrated proactive involvement in activities or responsibilities outside their defined role or support other BU with <b>high tangible impact*</b>, with consideration to their performance in main role and BU performance. The employee must be the leader and/or key person of the innovation implemented.</p>
                </div>
              </div>
              <p>*) measurable and meaningful results that directly contribute to business performance.</p>

              <p>For more detailed information, please download the document <a href='https://drive.google.com/drive/folders/1jH4AoHvPAcbgymeLnovqvQmCea35giNh?usp=sharing'>here</a></p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <>
              <ActionButtonGradient
                onClick={handleModalClose}
                variant='danger'
                children={"Close"}
              />
              
              <ActionButtonGradient
                onClick={() => {
                  setConfirmationAPlus(false)
                }}
                variant='primary'
                children={"Continue to Form"}
              />
            </>
          </Modal.Footer>
        </Modal>:
        <Modal show={show} onHide={handleModalClose} dialogClassName='spmo-justification-modal' backdrop="static" centered>
          <Modal.Header closeButton>
            {
              isAPlus ?
              <Modal.Title><b>Justification Form A+</b></Modal.Title>:
              <Modal.Title><b>Justification Form A, Increase 2 Rating</b></Modal.Title>
            }
          </Modal.Header>
          <Modal.Body>
            <Descriptions 
              bordered 
              size="small" 
              column={1}
              style={{ marginBottom: '20px' }}
              labelStyle={{ textAlign: 'left', width: '30%' }}
              contentStyle={{ textAlign: 'left', width: '70%' }}
            >
              <Descriptions.Item label="Employee Code">{user.Nik}</Descriptions.Item>
              <Descriptions.Item label="Employee Name">{user.Name}</Descriptions.Item>
              <Descriptions.Item label="Designation">{user.Designation}</Descriptions.Item>
            </Descriptions>
            
            <div align="left" className='mt-3'>
              <h5><b>Initiative</b></h5>
            </div>
            {
              items && payload?
              <Tabs
                activeKey={activeKey.toString()}
                tabPosition={"top"}
                onChange={onChangeActiveKey}
                items={Array.from({ length: items }, (_, index) => ({
                  key: (index + 1).toString(),
                  label: `Initiative ${index + 1}`,
                  children: (
                    <Initiative 
                      isAPlus={isAPlus}
                      payload={payload} 
                      index={index} 
                      deleteInitiative={onDeleteInitiative} 
                      handleOnChange={handleOnChanges}
                      watch={watch}  
                      handleFileChange={handleFileChange}
                      year={project.Year}
                    />
                  )
                }))}
              />:
              <></>
            }
          </Modal.Body>
          <Modal.Footer>
            {
              watch ?
              <>
                <ActionButtonGradient
                  onClick={handleModalClose}
                  variant='danger'
                  children={"Close"}
                />
              </>:
              <>
                <ActionButtonGradient
                  onClick={(e) => onClickAddInitiative(e)}
                  variant='primary'
                  disabled={isSaving}
                  children={"Add More"}
                />
                <ActionButtonGradient
                  onClick={handleSave}
                  variant='primary'
                  disabled={isSaving}
                  children={isSaving ? "Saving..." : "Save"}
                />
              </>
            }
          </Modal.Footer>
        </Modal>
      }
      
      {/* Unsaved Changes Confirmation Modal */}
      <EditConfirmationModal
        show={showUnsavedChangesModal}
        onHide={() => setShowUnsavedChangesModal(false)}
        onConfirm={handleConfirmSave}
        onCancel={handleDiscardChanges}
        title="Save Changes"
        message="You have made changes to an existing justification. Do you want to save these changes?"
        confirmText="Submit"
        cancelText="Cancel"
      />
    </>
  );
}

export default TopRemark